import { Box, Card, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import type { NavSubCategory } from '@/content/types'
import { productsBySubCategory, subCategoryPrimaryImage } from '@/content/products'
import { colors, fonts } from '@/theme'
import { Media } from '@/components/ui/Media'

/**
 * Subcategory tile (TIG/MIG, Stick under Welding Gloves). Mirrors CategoryCard's
 * image-forward treatment for visual consistency across product browsing.
 */
export function SubCategoryCard({ sub, index = 1 }: { sub: NavSubCategory; index?: number }) {
  const img = subCategoryPrimaryImage(sub.category, sub.slug)
  const count = productsBySubCategory(sub.category, sub.slug).length
  const num = String(index).padStart(2, '0')

  return (
    <Card
      component={RouterLink}
      to={`/products/${sub.category}/${sub.slug}`}
      sx={{
        position: 'relative',
        display: 'block',
        aspectRatio: '4 / 5',
        overflow: 'hidden',
        textDecoration: 'none',
        transition: 'border-color 300ms ease, transform 400ms cubic-bezier(.2,.8,.2,1), box-shadow 400ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-6px)',
          boxShadow: '0 24px 48px -24px rgba(0,0,0,0.8)',
        },
        '&:hover img': { transform: 'scale(1.06)' },
        '&:hover .sub-rule': { width: 64 },
        '&:hover .sub-cta': { color: colors.gold },
        '&:hover .sub-arrow': { backgroundColor: colors.gold, color: colors.bg, transform: 'translate(3px, -3px)' },
      }}
    >
      {img ? (
        <Media
          src={img.src}
          srcSm={img.srcSm}
          alt={sub.name}
          width={img.width}
          height={img.height}
          ratio={4 / 5}
          sx={{ position: 'absolute', inset: 0, height: '100%' }}
          imgSx={{ transition: 'transform 700ms cubic-bezier(.2,.8,.2,1)' }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: colors.surfaceAlt }} />
      )}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(13,13,13,0.55) 0%, rgba(13,13,13,0) 30%, rgba(13,13,13,0.08) 46%, rgba(13,13,13,0.78) 80%, rgba(13,13,13,0.96) 100%)',
        }}
      />

      <Stack sx={{ position: 'absolute', inset: 0, p: { xs: 2.5, md: 3 }, justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: fonts.label, fontSize: 13, fontWeight: 600, letterSpacing: '0.18em', color: colors.gold }}>
          {num}
        </Typography>

        <Box>
          <Box className="sub-rule" sx={{ width: 32, height: 2, bgcolor: colors.gold, mb: 1.75, transition: 'width 350ms cubic-bezier(.2,.8,.2,1)' }} />
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" component="h3" sx={{ lineHeight: 1.02 }}>
                {sub.name}
              </Typography>
              <Typography
                className="sub-cta"
                sx={{ mt: 1, fontFamily: fonts.label, fontSize: 13, letterSpacing: '0.04em', color: colors.grey, transition: 'color 300ms ease' }}
              >
                {count} product{count === 1 ? '' : 's'} · View range
              </Typography>
            </Box>
            <Box
              className="sub-arrow"
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: `1px solid ${colors.gold}`,
                color: colors.gold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 300ms ease, color 300ms ease, transform 300ms ease',
              }}
            >
              <ArrowUpRight size={20} />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Card>
  )
}
